import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronDown } from 'lucide-react';
import { useAtomValue } from 'jotai';
import {
  useSdnUser,
  InlineLoadingSpinner,
} from '@spins-gcp/shared-components-react';
import { useMimic } from '../../hooks/useMimic';
import { companiesAvailableAtom } from '../../atoms/companiesAvailableAtom';
import { companiesLoadingAtom } from '../../atoms/companiesLoadingAtom';
import styles from './portal-header.module.scss';

export function UserMenu() {
  const navigate = useNavigate();
  const { user } = useSdnUser();
  const { setShowMimicModal, isMimic, mimicUser } = useMimic();
  const companiesAvailable = useAtomValue(companiesAvailableAtom);
  const companiesLoading = useAtomValue(companiesLoadingAtom);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return <InlineLoadingSpinner size={10} />;

  return (
    <div ref={userMenuRef} className={styles['user-menu']}>
      <button
        data-testid="user-menu-button"
        className={styles['user-name']}
        onClick={() => setShowUserMenu((prev) => !prev)}
      >
        <div className={styles['user-name-info']}>
          <span className={styles['user-name-text']}>
            {isMimic && mimicUser
              ? mimicUser.name
              : `${user.firstName} ${user.lastName}`}
          </span>
          <span className={styles['user-company-text']}>
            {isMimic && mimicUser ? mimicUser.company : user.company.name}
          </span>
        </div>
        <span
          className={`${styles['chevron']} ${
            showUserMenu ? styles['chevron-up'] : ''
          }`}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </span>
      </button>

      {showUserMenu && (
        <div className={styles['user-dropdown']}>
          {!isMimic && (
            <OverlayTrigger
              placement="left"
              overlay={
                !companiesLoading && !companiesAvailable ? (
                  <Tooltip>You are not allowed to view as user</Tooltip>
                ) : (
                  <></>
                )
              }
            >
              <span>
                <button
                  data-testid="view-as-user-button"
                  className={styles['dropdown-item']}
                  disabled={companiesLoading || !companiesAvailable}
                  onClick={() => {
                    setShowMimicModal(true);
                    setShowUserMenu(false);
                  }}
                >
                  <span className={styles['dropdown-item-title']}>
                    <span>View as User</span>
                    {companiesLoading && (
                      <span className={styles['dropdown-item-spinner']}>
                        <InlineLoadingSpinner size={10} />
                      </span>
                    )}
                  </span>
                  <span className={styles['dropdown-item-subtitle']}>
                    See what a user sees
                  </span>
                </button>
              </span>
            </OverlayTrigger>
          )}
          <button
            className={styles['dropdown-item-sign-out']}
            onClick={() => navigate('/logout')}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
